import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LaborTime, LinkedReference, Reference } from '@1po/1po-bff-fe-spec/generated/estimate/response/model/Estimate';
import { pdf } from '@react-pdf/renderer';
import { ROUTER_ESTIMATE_HISTORY } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { CarIcon, ClockIcon, DMSExportIcon, ExclamationCircleIcon, InfoIcon, PrintIcon } from 'assets/icons';
import B2BComponent from 'components/B2BComponents/B2BComponent';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import { Dialog, InfoDialog, useDisclosure } from 'components/Dialog';
import SendEmailDialog from 'components/SendEmailDialog';
import { isCertasOil } from 'components/StockInfo/catalogStockUtils';
import { useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import {
  getEmailFromGarageData,
  setEmailFromGarageAttachments,
  setEmailFromGarageData,
} from 'domains/email/Email.store';
import {
  addCatalogReferencesToBasket,
  addVehicle,
  getCatalogOperationReferencesLength,
  getCatalogReferencesLength,
  getEstimateB2BStocks,
  getEstimateById,
  getEstimateVehicle,
  sendB2BEstimateToClientHistoryRequest,
  sendEstimateToDMSRequest,
  setOrderDialogOpen,
} from 'domains/estimate/Estimate.store';
import { getUserGarageWorkshopId } from 'domains/garage/Garage.store';
import { getStocksMapV2 } from 'domains/references';
import { useFetchReferencesStocks } from 'domains/references/References.requests';
import { getUserCommercialLink, getUserDisplayMode } from 'domains/user';
import { CatalogButton, ChangeVehiclePopup } from 'pages/CatalogPage/common';
import { SFlex } from 'pages/CatalogPage/DH/Maintenance/Maintenance.styled';
import { PdfFilterProps } from 'pages/EstimatePage/EstimateModifyTab';
import { useIsValidB2BEstimateToOrder } from 'pages/EstimatePage/OrderEstimate/useIsValidB2BEstimateToOrder';
import { useEstimatePdfData, useIsWholeEstimateDataValid } from 'pages/EstimatePage/useEstimateData';
import { EstimatePdfData } from 'pages/EstimatePdf';
import { EstimatePage } from 'pages/EstimatePdf/EstimatePdf';
import { EstimatePdfBrand } from 'pages/EstimatePdf/EstimatePdfPage';
import { theme } from 'styles';
import {
  BlueButton,
  Box,
  CartArrowDownButtonRound,
  Checkbox,
  DMSExportButtonRound,
  Flex,
  Icon,
  IconButton,
  MarginBox,
  MessageButtonRound,
  PrintButtonRound,
  SendButtonRound,
  Switch,
  Text,
  WarningAlert,
  WhiteButton,
  YellowButton,
} from 'UI';
import { ERROR, FOUND, getData, hasData, LOADING } from 'utils';
import { TRACKING_EVENT_TRANSFER_ESTIMATE_TO_CART } from 'utils/eventTracker/EventTracker.types';
import { formatVehicleBrand } from 'utils/mappers';

export const CART_CLICK = 'cart';
export const DMS_CLICK = 'dms';

const EPREL_READY_TIMEOUT_MS = 3000;

export type BUTTON_CLICK = undefined | typeof CART_CLICK | typeof DMS_CLICK;

export interface EstimateModifyTabButtonsSectionProps {
  estimateId: string;
  renderAddVehicle?: boolean;
  isDMSAvailable: boolean | typeof LOADING;
}

const EstimatePrintWrapper = ({ estimateId }: { estimateId: string }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [printPdfFilter, setPrintPdfFilter] = useState<PdfFilterProps>({
    hideReferenceNumbers: false,
    hideDiscounts: false,
  });
  const emailData = useSelector(getEmailFromGarageData);
  const commercialLink = useSelector(getUserCommercialLink);
  const isR3Motrio = useSelector(getUserDisplayMode) === 'R3_MOTRIO';
  const brand: EstimatePdfBrand = isR3Motrio ? 'motrio' : 'renault';
  const isB2B = useIsB2B();
  const disclosurePrint = useDisclosure();
  const { onOpen: onOpenPrint } = disclosurePrint;
  const isEstimateComplete = useIsWholeEstimateDataValid(estimateId);
  const [printWithIncompleteFieldsChecked, setPrintWithIncompleteFieldsChecked] = useState(isEstimateComplete);
  const { data: pdfPrintData, isEprelReady: isPrintEprelReady } = useEstimatePdfData(estimateId, true);
  const { data: pdfData, isEprelReady: isEmailEprelReady } = useEstimatePdfData(estimateId, false);
  const printReadyResolverRef = useRef<((readyData: EstimatePdfData) => void) | null>(null);

  useEffect(() => {
    setPrintWithIncompleteFieldsChecked(isEstimateComplete);
  }, [isEstimateComplete]);

  useEffect(() => {
    if (isPrintEprelReady && printReadyResolverRef.current) {
      printReadyResolverRef.current(pdfPrintData);
      printReadyResolverRef.current = null;
    }
  }, [isPrintEprelReady, pdfPrintData]);

  useEffect(() => {
    const recipient = pdfData.client?.email ?? '';
    const clientName = pdfData.client?.name ?? '';
    const brandModel = `${formatVehicleBrand(pdfData.vehicleDetail?.vehicleBrand) ?? ''} ${
      pdfData.vehicleDetail?.model ?? ''
    }`;
    const garageName = commercialLink?.name ?? '';

    dispatch(
      setEmailFromGarageData({
        ...emailData,
        recipients: [recipient],
        subject: t('estimate.email.subject.your_estimate', 'Your estimate'),
        body:
          `${t('estimate.email.dear_client_name', 'Dear {{clientName}},', { clientName })}\n` +
          `${t('estimate.email.estimate_for_model', 'Please find attached your estimate for your {{brandModel}}.', {
            brandModel,
          })}\n` +
          `${t('common.best_regards', 'Best regards,')}\n` +
          `${garageName}`,
      }),
    );
    // eslint-disable-next-line
  }, [pdfData.client, estimateId, t]);

  const setPdfAttachment = async () => {
    const pdfBlob = await getEmailPdfDataBlob();
    const reader = new FileReader();
    reader.readAsDataURL(pdfBlob);
    reader.onloadend = () => {
      const base64data = reader?.result?.toString().replace(/^.*,/, '');
      if (base64data) {
        dispatch(
          setEmailFromGarageAttachments([
            {
              fileName: `${t('estimate', 'Estimate')}.pdf`,
              fileBase64: base64data,
            },
          ]),
        );
      }
    };
  };

  useEffect(() => {
    setPdfAttachment().catch(() => setEmailFromGarageAttachments(ERROR));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printPdfFilter.hideDiscounts, printPdfFilter.hideReferenceNumbers, isEmailEprelReady]);
  const getHideReferencesCheckbox = () => (
    <Checkbox
      checked={printPdfFilter.hideReferenceNumbers}
      onChange={() => {
        dispatch(setEmailFromGarageAttachments(LOADING));
        setPrintPdfFilter((prevState) => {
          return {
            ...prevState,
            hideReferenceNumbers: !prevState.hideReferenceNumbers,
          };
        });
      }}
      label={
        <>
          <MarginBox ml={10} />
          <Text type={'text'}>{t('estimate.hide_information.references', 'Hide all references')}</Text>
        </>
      }
    />
  );

  const getHideDiscountsCheckbox = () => (
    <Checkbox
      checked={printPdfFilter.hideDiscounts}
      onChange={() => {
        dispatch(setEmailFromGarageAttachments(LOADING));
        setPrintPdfFilter((prevState) => {
          return {
            ...prevState,
            hideDiscounts: !prevState.hideDiscounts,
          };
        });
      }}
      label={
        <>
          <MarginBox ml={10} />
          <Text type={'text'}>{t('estimate.hide_information.discounts', 'Hide all discounts')}</Text>
        </>
      }
    />
  );

  const getAdditionalOptions = () => (
    <Flex justify={'flex-end'} gap={20} align={'flex-end'} size={1}>
      {getHideReferencesCheckbox()}
      {getHideDiscountsCheckbox()}
    </Flex>
  );

  const isEmailEnabled = () => {
    const hasDesignation = (data: (LaborTime | Reference | LinkedReference)[]) => data.some((d) => d.designation);

    const isAnyValidReference = hasDesignation(pdfData.reference);
    const isAnyValidLaborTime = hasDesignation(pdfData.laborTime);
    const isAnyValidOperation = pdfData.operations.some(
      (op) => hasDesignation(op.referenceSubsection.references) || hasDesignation(op.laborTimeSubsection.laborTimes),
    );

    return isAnyValidReference || isAnyValidLaborTime || isAnyValidOperation;
  };

  const generateEstimatePdfBlob = (data: EstimatePdfData): Promise<Blob> =>
    pdf(<EstimatePage data={data} pdfFilter={printPdfFilter} brand={brand} isB2B={isB2B} />).toBlob();

  const waitForReadyPrintData = (timeoutMs: number): Promise<EstimatePdfData> =>
    new Promise((resolve) => {
      if (isPrintEprelReady) {
        resolve(pdfPrintData);
        return;
      }
      const timeoutId = setTimeout(() => {
        printReadyResolverRef.current = null;
        resolve(pdfPrintData);
      }, timeoutMs);
      printReadyResolverRef.current = (readyData) => {
        clearTimeout(timeoutId);
        resolve(readyData);
      };
    });

  const handlePrint = async () => {
    const readyPrintData = await waitForReadyPrintData(EPREL_READY_TIMEOUT_MS);
    const pdfBlob = await generateEstimatePdfBlob(readyPrintData);
    const url = URL.createObjectURL(pdfBlob);
    if (url) {
      window.open(url);
    }
  };

  const openSendEmail = {
    element: (
      <MessageButtonRound
        onClick={() => setPdfAttachment().catch(() => setEmailFromGarageAttachments(ERROR))}
        disabled={!isEmailEnabled()}
        size={20}
        boxSize={'sm'}
      />
    ),
    disabled: !isEmailEnabled(),
  };

  const getEmailPdfDataBlob = async () => generateEstimatePdfBlob(pdfData);

  return (
    <>
      <MarginBox my={'auto'}>
        <SendEmailDialog
          title={t('estimate.email.title', 'Send estimate by email')}
          triggerElement={openSendEmail}
          additionalOptions={getAdditionalOptions()}
        />
      </MarginBox>
      <MarginBox my={'auto'}>
        <PrintButtonRound
          onClick={() => {
            onOpenPrint();
            setPrintWithIncompleteFieldsChecked(isEstimateComplete);
          }}
          size={20}
          boxSize={'sm'}
        />
        <Dialog
          disclosure={disclosurePrint}
          title={t('estimate.hide_information.title2', 'Print Estimate')}
          icon={PrintIcon}
          handleCancel={() => {
            setPrintWithIncompleteFieldsChecked(isEstimateComplete);
          }}
          isConfirmButtonDisabled={!(isEstimateComplete || printWithIncompleteFieldsChecked)}
          content={
            <>
              {!isEstimateComplete && (
                <>
                  <Text type={'light_14'}>
                    {t(
                      'estimate.print_incompatible.info',
                      'You are about to print an incomplete estimation. Would you like to print the document regardless?',
                    )}
                  </Text>
                  <Box height={10} />
                </>
              )}
              {!isEstimateComplete && (
                <WarningAlert
                  size={'lg'}
                  backgroundColor={theme.color['grey_very_light_2']}
                  icon={<Icon size={24} IconComponent={ExclamationCircleIcon} color={theme.color.brand_black_dark} />}
                  message={
                    <SFlex align={'center'} direction={'row'}>
                      <SFlex direction={'column'}>
                        <Text type="base" bold>
                          {t('estimate.print_incompatible.title', 'Print with Incomplete fields')}
                        </Text>
                        <Box height={4} />
                        <Text type="base">
                          {t(
                            'estimate.print_incompatible.description',
                            'Some information may be incomplete. Proceed with printing?',
                          )}
                        </Text>
                      </SFlex>
                      <Switch
                        checked={printWithIncompleteFieldsChecked}
                        onChange={() => setPrintWithIncompleteFieldsChecked((p) => !p)}
                      />
                    </SFlex>
                  }
                />
              )}
              <SFlex align={'center'} gap={20} direction={'row'} justify={'flex-start'}>
                <Text type="base" bold>
                  {t('', 'Options:')}
                </Text>
                {getHideReferencesCheckbox()}
                {getHideDiscountsCheckbox()}
              </SFlex>
            </>
          }
          handleConfirm={handlePrint}
        />
      </MarginBox>
    </>
  );
};
const EstimateModifyTabButtonsSection = ({
  estimateId,
  renderAddVehicle,
  isDMSAvailable,
}: EstimateModifyTabButtonsSectionProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const dmsWorkshopId = useSelector(getUserGarageWorkshopId);
  const [buttonClickType, setButtonClickType] = useState<BUTTON_CLICK>(undefined);
  const [activeAddToCart, setActiveAddToCart] = useState(false);
  const isVehicleFound = useRef(false);
  const disclosure = useDisclosure();
  const { onOpen, onClose } = disclosure;
  const isValidToOrder = useIsValidB2BEstimateToOrder(estimateId);
  const [isAddVehiclePopupVisible, setIsAddVehiclePopupVisible] = useState(false);
  const catalogReferencesCount = useSelector((state: RootState) => getCatalogReferencesLength(state, estimateId));
  const operationReferenesCount = useSelector((state: RootState) =>
    getCatalogOperationReferencesLength(state, estimateId),
  );

  const vehicle = useSelector((state: RootState) => getEstimateVehicle(state, estimateId));
  const estimate = useSelector((state: RootState) => getEstimateById(state, estimateId));
  const isB2B = useIsB2B();

  useEffect(() => {
    setActiveAddToCart(catalogReferencesCount + operationReferenesCount > 0);
  }, [catalogReferencesCount, operationReferenesCount]);

  const handleExportToDMS = () => {
    if (!hasData(estimate)) {
      return;
    }
    if (estimate.dmsExportTime) {
      setButtonClickType(DMS_CLICK);
      onOpen();
    } else {
      exportToDms();
    }
  };

  const exportToDms = () => {
    dispatch(sendEstimateToDMSRequest());
    onClose();
  };

  const references = getData(estimate)?.referenceSubsection?.references;
  const isAnyChunkOilReference = () => {
    return references?.some((ref) => isCertasOil(ref.commercialFamily));
  };

  const handleEstimateOrder = () => {
    dispatch(setOrderDialogOpen(true));
  };

  const oilReferenceNumbers =
    references
      ?.filter(isAnyChunkOilReference)
      .map((ref) => ref.referenceNumber)
      .filter((referenceNumber): referenceNumber is string => Boolean(referenceNumber)) || [];
  useFetchReferencesStocks(oilReferenceNumbers);
  const isOilUser = useIsOilChunkUserR1();

  const realStocks = useSelector((state: RootState) => getStocksMapV2(state, oilReferenceNumbers));
  const b2bStocks = useSelector((state: RootState) => getEstimateB2BStocks(state, estimateId));
  const stocks = isB2B ? realStocks : b2bStocks;
  const stockStatus = useMemo(() => [...stocks.values()].map((stock) => stock?.searchStatus), [stocks]);
  const isR1Stock =
    stockStatus.includes(FOUND) &&
    [...stocks.values()].some((stock) => {
      const stockData = stock?.data;
      if (!stockData) return false;
      return [...stockData.values()].some((referenceStock) =>
        referenceStock.warehouses?.some((warehouse) => warehouse.type === 'LOCAL' && warehouse.confirmedQuantity > 0),
      );
    });

  const handleAddToCartClick = () => {
    setButtonClickType(CART_CLICK);
    if (isOilUser && !isR1Stock && isAnyChunkOilReference()) {
      onOpen();
    }
    if (catalogReferencesCount + operationReferenesCount === 0) {
      return;
    }

    dispatch(addCatalogReferencesToBasket());
    setActiveAddToCart(false);
    trackAppEvent(TRACKING_EVENT_TRANSFER_ESTIMATE_TO_CART);
  };

  const handleFound = useCallback(() => {
    if (!isVehicleFound.current) {
      dispatch(addVehicle());
      isVehicleFound.current = true;
    }
  }, [dispatch]);

  const sendToEstimateClientHistoryClick = () => {
    if (isB2B) {
      dispatch(sendB2BEstimateToClientHistoryRequest({ estimateId }));
    }
  };

  return (
    <Flex size={1} justify={'flex-end'} gap={10}>
      {isAnyChunkOilReference() && buttonClickType === CART_CLICK && (
        <InfoDialog
          title={'Information'}
          handleConfirm={() => undefined}
          disclosure={disclosure}
          icon={InfoIcon}
          description={[
            t('catalog.universal_products.estimate_add_to_cart.first_line', 'Castrol Oil quantities have been changed'),
            t('catalog.universal_products.estimate_add_to_cart.second_line', 'to comply with minimum order'),
          ]}
          content={
            <Flex minWidth={400} direction={'row-reverse'}>
              <YellowButton onClick={onClose}>
                <Flex minWidth={80} justify={'center'}>
                  {t('common.ok', 'OK')}
                </Flex>
              </YellowButton>
            </Flex>
          }
        />
      )}
      {buttonClickType === DMS_CLICK && (
        <InfoDialog
          width={850}
          title={'Action required'}
          handleConfirm={() => undefined}
          closable={false}
          maskClosable={false}
          disclosure={disclosure}
          icon={DMSExportIcon}
          description={[
            t(
              'estimate.dms.export.repeat.title',
              'This estimate has already been sent to your DMS. Do you really want to export the estimate again? ',
            ),
            t(
              'estimate.dms.export.repeat.second_line',
              'If so, please check the quantities of each item in your DMS after the export',
            ),
          ]}
          content={
            <Flex minWidth={700} maxWidth={700} direction={'row-reverse'} gap={30}>
              <BlueButton onClick={exportToDms}>
                <Flex minWidth={80} justify={'center'}>
                  {t('common.action.confirm', 'Confirm')}
                </Flex>
              </BlueButton>
              <WhiteButton onClick={onClose}>
                <Flex minWidth={80} justify={'center'}>
                  {t('common.action.cancel', 'Cancel')}
                </Flex>
              </WhiteButton>
            </Flex>
          }
        />
      )}
      <B2BComponent
        b2c={
          <>
            {vehicle === undefined && renderAddVehicle && (
              <MarginBox my={'auto'}>
                <CatalogButton
                  id={'change_vehicle_button_estimate'}
                  data-cy={'add-vehicle-button'}
                  tooltip={t('estimate.action.add_vehicle', 'Add vehicle')}
                  noMargin
                  icon={CarIcon}
                  content={
                    <ChangeVehiclePopup
                      visible={isAddVehiclePopupVisible}
                      setIsChangeVehiclePopupVisible={setIsAddVehiclePopupVisible}
                      onFound={handleFound}
                    />
                  }
                  contentTitle={t('catalog.parts.action.change_vehicle', 'Change vehicle')}
                  isPopoverVisible={isAddVehiclePopupVisible}
                  onPopoverVisibleChange={(visible: boolean) => setIsAddVehiclePopupVisible(visible)}
                />
              </MarginBox>
            )}
          </>
        }
      />
      {dmsWorkshopId && hasData(estimate) && isDMSAvailable && (
        <MarginBox my={'auto'}>
          <DMSExportButtonRound
            dmsExportTime={estimate.dmsExportTime}
            onClick={handleExportToDMS}
            size={20}
            boxSize={'sm'}
          />
        </MarginBox>
      )}
      <MarginBox my={'auto'}>
        <B2BComponent
          b2b={
            <Flex gap={10}>
              <CartArrowDownButtonRound
                onClick={handleEstimateOrder}
                disabled={!isValidToOrder.isValid}
                color={'selected'}
                bgColor={'success_light_130'}
                hoverColor={'selected'}
                hoverFill={'success_light_130'}
                tooltip={!isValidToOrder.isValid ? isValidToOrder.message : t('common.action.order', 'Order')}
                size={20}
                boxSize={'sm'}
                data-cy={'estimate-order-button'}
              />
              <SendButtonRound
                disabled={!isValidToOrder.isValid}
                tooltip={t('estimate.actions.send_to_client_history', 'Send to R2/R3/R3M History')}
                onClick={sendToEstimateClientHistoryClick}
                data-cy={'send-to-history-button'}
                size={20}
                boxSize={'sm'}
              />
            </Flex>
          }
          b2c={
            <CartArrowDownButtonRound
              onClick={handleAddToCartClick}
              disabled={!activeAddToCart}
              data-cy={'add-to-cart-button'}
              size={20}
              boxSize={'sm'}
            />
          }
        />
      </MarginBox>
      <EstimatePrintWrapper estimateId={estimateId} />
      <MarginBox my={'auto'} mr={30}>
        <IconButton
          IconComponent={ClockIcon}
          round
          size={20}
          boxSize={'sm'}
          color={'grey15'}
          bgColor={'grey95'}
          hoverFill={'grey75'}
          tooltip={t('estimate.history', 'History')}
          onClick={() => history.push(ROUTER_ESTIMATE_HISTORY)}
          data-cy={'history-button'}
        />
      </MarginBox>
    </Flex>
  );
};
export default EstimateModifyTabButtonsSection;
